import { HttpClient } from "@angular/common/http";
import { Inject, Injectable } from '@angular/core';
import { Conference } from "calendar";
import { I18nService } from "i18n";
import { BehaviorSubject, lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map, skip, take } from "rxjs/operators";
import { ENVIRONMENT, Logger } from 'shared';

@Injectable({
  providedIn: 'root'
})
export class JitsiService {

  domain: string = this.environment.conferenceDomain || 'meet.mnlo.app'; //'meet.jit.si';
  protected loaded = new BehaviorSubject<boolean>(false);
  protected loading = false;
  protected logger = new Logger('JitsiService');

  constructor(private httpClient: HttpClient, 
              private i18nService: I18nService,
              @Inject(ENVIRONMENT) private environment: any) {}

  load(): Observable<boolean> {
    if (this.loaded.getValue()) {
      return this.loaded.asObservable().pipe(take(1));
    }

    if (this.loading) {
      return this.loaded.asObservable().pipe(
        skip(1), // Skip current value
        take(1)
      );
    } else {
      this.loading = true;

      const script = document.createElement('script');
      script.src = `https://${this.domain}/external_api.js`;
      script.async = true;

      script.onload = () => {
        this.logger.debug('Jitsi Meet External API script loaded');
        this.loading = false;
        this.loaded.next(true);
      };

      script.onerror = (error) => {
        this.logger.error('Failed to load Jitsi Meet External API script');
        this.loading = false;
        this.loaded.error(error);
      };

      document.head.appendChild(script);
      return this.loaded.asObservable().pipe(
        skip(1), // Skip initial value
        take(1)
      );
    }
  }

  isLoaded(): boolean {
    return this.loaded.getValue();
  }

  createJwt(conference: Conference): Promise<string> {
    const language = this.i18nService.getLanguage();
    return lastValueFrom(
      this.httpClient.post<any>(`/conference/token`, {
        id: conference.id, 
        source: conference.properties?.source,
        locale: language, 
        area: 'conference'
      })
      .pipe(
        map(response => response.token),
        catchError(error => {
          this.logger.error(error);
          return throwError(() => error);
        })
    ))
  }
}
